import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import {
  addressesApi,
  clientsApi,
  integrationsApi,
  zonesApi,
  type Address,
  type AddressImportPreviewRow,
  type AddressImportPreview,
  type AddressPrediction,
} from '@/api/client';
import { useAsync, useDebounce } from '@/hooks';
import { Layout } from '@/components/Layout';
import { DataTable } from '@/components/DataTable';
import { Input } from '@/components/Input';
import { Select } from '@/components/Select';
import { Button } from '@/components/Button';
import { Modal } from '@/components/Modal';
import { adminNavItems } from '@/pages/navItems';
import { useConfirm, useToast } from '@/components/Feedback';
import { humanize } from '@/lib/format';
import { FlashMessage, errorText, useFlash } from '@/components/FlashMessage';

const CSV_TEMPLATE = [
  'label,full_address,contact_person,contact_number,latitude,longitude',
  'Main Warehouse,123 Industrial Ave Brgy San Isidro Makati City,Juan Dela Cruz,+639171234567,,',
].join('\n');

const EMPTY_ADDRESS_FORM = {
  clientId: '',
  label: '',
  line1: '',
  cityMunicipality: '',
  province: '',
  postalCode: '',
  contactPerson: '',
  contactNumber: '',
  isInternalHub: false,
};

function composeFullAddress(f: typeof EMPTY_ADDRESS_FORM): string {
  return [f.line1, f.cityMunicipality, f.province, f.postalCode]
    .map((part) => part.trim())
    .filter(Boolean)
    .join(', ');
}

export function AdminAddresses() {
  const { data: clients } = useAsync(() => clientsApi.list(), []);
  const { data: zones } = useAsync(() => zonesApi.list(), []);

  const [filterClientId, setFilterClientId] = useState('');
  const { data: addresses, loading, error: loadError, reload } = useAsync(
    () => addressesApi.list(filterClientId || undefined),
    [filterClientId],
  );

  const clientMap = useMemo(
    () => Object.fromEntries((clients ?? []).map((c) => [c.id, c.legalName])),
    [clients],
  );
  const zoneMap = useMemo(
    () => Object.fromEntries((zones ?? []).map((z) => [z.id, z.code])),
    [zones],
  );

  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState<Address | null>(null);
  const [form, setForm] = useState(EMPTY_ADDRESS_FORM);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const confirm = useConfirm();
  /** Address text the form opened with — no autocomplete lookups until it is changed. */
  const initialLine1 = useRef('');
  const [saving, setSaving] = useState(false);
  const { flash, ok: showOk, warn: showWarn } = useFlash();
  const toast = useToast();
  const [error, setError] = useState('');

  const [importClientId, setImportClientId] = useState('');
  const [csvText, setCsvText] = useState('');
  const [preview, setPreview] = useState<AddressImportPreview | null>(null);
  const [importing, setImporting] = useState(false);
  const { flash: importFlash, ok: importOk, error: importError, clear: importClear } = useFlash();
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [sessionToken, setSessionToken] = useState(() => crypto.randomUUID());
  const [predictions, setPredictions] = useState<AddressPrediction[]>([]);
  const [autocompleteLoading, setAutocompleteLoading] = useState(false);
  const debouncedLine1 = useDebounce(form.line1, 300);

  useEffect(() => {
    const query = debouncedLine1.trim();
    // Guardrails: never call out on every keystroke — debounce 300ms (above) and
    // require at least 4 characters, same limits Google bills Places Autocomplete against.
    if (!showAdd || query.length < 4 || query === initialLine1.current.trim()) {
      setPredictions([]);
      return;
    }
    let cancelled = false;
    setAutocompleteLoading(true);
    integrationsApi
      .autocomplete(query, sessionToken)
      .then((res) => {
        if (!cancelled) setPredictions(res.predictions);
      })
      .catch(() => {
        if (!cancelled) setPredictions([]);
      })
      .finally(() => {
        if (!cancelled) setAutocompleteLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [debouncedLine1, sessionToken, showAdd]);

  const selectPrediction = (prediction: AddressPrediction) => {
    setForm((f) => ({ ...f, line1: prediction.description }));
    setPredictions([]);
    // A new session token starts as soon as a selection is made — this is one
    // complete autocomplete "search session" for Google's session-token billing model.
    setSessionToken(crypto.randomUUID());
  };

  const openAdd = () => {
    setEditing(null);
    initialLine1.current = '';
    setForm({ ...EMPTY_ADDRESS_FORM, clientId: filterClientId || '' });
    setPredictions([]);
    setSessionToken(crypto.randomUUID());
    setError('');
    setShowAdd(true);
  };

  const openEdit = (row: Address) => {
    setEditing(row);
    initialLine1.current = row.fullAddress;
    setForm({
      ...EMPTY_ADDRESS_FORM,
      clientId: row.clientId ?? '',
      label: row.label,
      line1: row.fullAddress,
      contactPerson: row.contactPerson ?? '',
      contactNumber: row.contactNumber ?? '',
      isInternalHub: Boolean(row.isInternalHub),
    });
    setPredictions([]);
    setSessionToken(crypto.randomUUID());
    setError('');
    setShowAdd(true);
  };

  const removeAddress = async (row: Address) => {
    const ok = await confirm({
      title: `Delete ${row.label}?`,
      message:
        'It will no longer appear in the address book or when booking. ' +
        'Trips that already use this address keep it.',
      confirmLabel: 'Delete address',
      danger: true,
    });
    if (!ok) return;
    setDeletingId(row.id);
    try {
      await addressesApi.remove(row.id);
      toast(`${row.label} deleted`);
      showOk(`${row.label} was removed from the address book. Trips that already use it keep it.`);
      reload();
    } catch (err) {
      showWarn(errorText(err, `Could not delete ${row.label}.`));
    } finally {
      setDeletingId(null);
    }
  };

  const save = async (e: FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const fullAddress = composeFullAddress(form);
      if (!fullAddress) throw { message: 'Enter at least a street line or city' };
      const isInternalHub = !form.clientId;
      const created = editing
        ? await addressesApi.update(editing.id, {
            clientId: form.clientId || null,
            label: form.label.trim(),
            fullAddress,
            contactPerson: form.contactPerson.trim() || null,
            contactNumber: form.contactNumber.trim() || null,
            isInternalHub,
          })
        : await addressesApi.create({
            clientId: form.clientId || null,
            label: form.label.trim(),
            fullAddress,
            contactPerson: form.contactPerson || undefined,
            contactNumber: form.contactNumber || undefined,
            isInternalHub,
          });
      setShowAdd(false);
      setEditing(null);
      const label = form.label.trim();
      toast(`${label} saved`);
      const zoneCode = created?.zoneId ? zoneMap[created.zoneId] : undefined;
      if (created && (created.latitude == null || created.longitude == null)) {
        showWarn(`${label} was saved, but the address couldn't be found on the map. Check the spelling — trips to it can't be priced by zone.`);
      } else if (created && !created.zoneId) {
        showWarn(`${label} was saved, but it is outside all service zones. Trips to it will need a manual quote.`);
      } else {
        showOk(
          `${label} saved${zoneCode ? ` in zone ${zoneCode}` : ''}${isInternalHub ? ' as an internal hub' : ''}.`,
        );
      }
      reload();
    } catch (err) {
      setError((err as { message?: string }).message ?? 'Failed to save address');
    } finally {
      setSaving(false);
    }
  };

  const downloadTemplate = () => {
    const blob = new Blob([CSV_TEMPLATE], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'address-book-template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const onFileChosen = async (file: File) => {
    const text = await file.text();
    setCsvText(text);
    setPreview(null);
    importClear();
  };

  const runPreview = async () => {
    if (!importClientId) {
      importError('Select a client to import addresses for');
      return;
    }
    if (!csvText.trim()) {
      importError('Upload or paste CSV content first');
      return;
    }
    setImporting(true);
    importClear();
    try {
      const result = await addressesApi.importPreview(importClientId, csvText);
      setPreview(result);
    } catch (err) {
      importError(errorText(err, 'Could not check the file.'));
    } finally {
      setImporting(false);
    }
  };

  const confirmImport = async () => {
    if (!importClientId) return;
    setImporting(true);
    importClear();
    try {
      const res = await addressesApi.import(importClientId, csvText);
      importOk(`Imported ${res.imported} address${res.imported === 1 ? '' : 'es'}. Check the Zone column for any outside your service zones.`);
      toast(`Imported ${res.imported} address${res.imported === 1 ? '' : 'es'}`);
      setPreview(null);
      setCsvText('');
      if (fileInputRef.current) fileInputRef.current.value = '';
      reload();
    } catch (err) {
      importError(errorText(err, 'Could not import the addresses.'));
    } finally {
      setImporting(false);
    }
  };

  const columns = [
    { key: 'label', header: 'Label' },
    { key: 'fullAddress', header: 'Address' },
    {
      key: 'client',
      header: 'Client',
      value: (row: Address) =>
        row.isInternalHub
          ? 'Internal hub'
          : (row.clientId ? clientMap[row.clientId] ?? '' : ''),
      render: (row: Address) =>
        row.isInternalHub ? 'Internal hub' : (row.clientId ? clientMap[row.clientId] ?? '—' : '—'),
    },
    {
      key: 'zone',
      header: 'Zone',
      value: (row: Address) => (row.zoneId ? zoneMap[row.zoneId] ?? '' : ''),
      render: (row: Address) =>
        row.zoneId ? (zoneMap[row.zoneId] ?? '—') : row.latitude != null ? 'Outside service zones' : '—',
    },
    {
      key: 'coords',
      header: 'Lat / Lng',
      value: (row: Address) =>
        row.latitude != null && row.longitude != null ? row.latitude : null,
      render: (row: Address) =>
        row.latitude != null && row.longitude != null
          ? `${row.latitude.toFixed(4)}, ${row.longitude.toFixed(4)}`
          : 'Not found on map',
    },
    {
      key: 'contact',
      header: 'Contact',
      value: (row: Address) => row.contactPerson ?? '',
      render: (row: Address) => row.contactPerson ?? '—',
    },
    {
      key: 'actions',
      header: '',
      sortable: false,
      render: (row: Address) => (
        <div className="row" style={{ gap: 'var(--space-xs)', justifyContent: 'flex-end' }}>
          <Button type="button" size="sm" variant="secondary" onClick={() => openEdit(row)}>
            Edit
          </Button>
          <Button
            type="button"
            size="sm"
            variant="secondary"
            loading={deletingId === row.id}
            onClick={() => void removeAddress(row)}
          >
            Delete
          </Button>
        </div>
      ),
    },
  ];

  return (
    <Layout navItems={adminNavItems}>
      <div className="page">
        <div className="page-header row-between">
          <div>
            <p className="kicker">Clients</p>
            <h1>Address book</h1>
            <p className="text-muted">
              Saved pickup and delivery points. Each address is placed on the map and matched to a service zone when saved.
            </p>
          </div>
          <Button onClick={openAdd}>Add address</Button>
        </div>

        <FlashMessage flash={flash} />

        <div className="filter-row">
          <Select
            label="Filter by client"
            value={filterClientId}
            onChange={(e) => setFilterClientId(e.target.value)}
          >
            <option value="">All clients</option>
            {(clients ?? []).map((c) => (
              <option key={c.id} value={c.id}>{c.legalName}</option>
            ))}
          </Select>
        </div>

        <DataTable
          columns={columns}
          data={addresses ?? []}
          keyField="id"
          loading={loading}
          loadError={loadError}
          dense
          searchPlaceholder="Filter addresses…"
          emptyMessage="No addresses yet. Add one or import a CSV below."
        />

        <section className="panel">
          <div className="row-between" style={{ marginBottom: '1rem' }}>
            <div>
              <h2>CSV import</h2>
              <p className="text-muted">
                Import a client's saved addresses in bulk. Each row is placed on the map and matched
                to a service zone.
              </p>
            </div>
            <Button size="sm" variant="secondary" onClick={downloadTemplate}>Download template</Button>
          </div>

          <div className="import-grid">
            <Select
              label="Client"
              value={importClientId}
              onChange={(e) => {
                setImportClientId(e.target.value);
                setPreview(null);
              }}
              required
            >
              <option value="">Select client…</option>
              {(clients ?? []).map((c) => (
                <option key={c.id} value={c.id}>{c.legalName}</option>
              ))}
            </Select>

            <div className="input-group">
              <label className="input-label">CSV file</label>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,text/csv"
                className="input-field"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) onFileChosen(file);
                }}
              />
            </div>
          </div>

          <textarea
            className="csv-textarea"
            placeholder="label,full_address,contact_person,contact_number,latitude,longitude"
            value={csvText}
            onChange={(e) => {
              setCsvText(e.target.value);
              setPreview(null);
            }}
            rows={5}
          />

          <div className="row" style={{ gap: '0.5rem' }}>
            <Button variant="secondary" loading={importing} onClick={runPreview}>Preview</Button>
            <Button loading={importing} disabled={!preview?.rows.length || (preview.errors?.length ?? 0) > 0} onClick={confirmImport}>
              Confirm import
            </Button>
          </div>

          <FlashMessage flash={importFlash} />

          {preview && (
            <>
              {(preview.errors?.length ?? 0) > 0 && (
                <div className="form-error" style={{ marginBottom: 'var(--space-md)' }}>
                  {preview.errors!.map((e) => (
                    <div key={`${e.row}-${e.field ?? 'row'}`}>
                      Row {e.row}{e.field ? ` (${e.field})` : ''}: {e.message}
                    </div>
                  ))}
                </div>
              )}
            <DataTable
              dense
              keyField="label"
              data={preview.rows}
              emptyMessage="No rows parsed from CSV"
              columns={[
                { key: 'label', header: 'Label' },
                { key: 'fullAddress', header: 'Address' },
                { key: 'contactPerson', header: 'Contact' },
                {
                  key: 'zoneCode',
                  header: 'Zone',
                  render: (r: AddressImportPreviewRow) => r.zoneCode ?? (r.latitude != null ? 'Outside service zones' : 'Not found on map'),
                },
                {
                  key: 'coords',
                  header: 'Lat / Lng',
                  render: (r: AddressImportPreviewRow) =>
                    r.latitude != null && r.longitude != null
                      ? `${r.latitude.toFixed(5)}, ${r.longitude.toFixed(5)}`
                      : '—',
                },
                {
                  key: 'precision',
                  header: 'Precision',
                  render: (r: AddressImportPreviewRow) => (r.coordinatePrecision ? humanize(r.coordinatePrecision) : '—'),
                },
                {
                  key: 'change',
                  header: 'Change',
                  render: (r: AddressImportPreviewRow) => (r.change === 'new' ? 'New' : 'Update'),
                },
              ]}
            />
            </>
          )}
        </section>

        <Modal
          open={showAdd}
          onClose={() => { setShowAdd(false); setEditing(null); }}
          title={editing ? `Edit ${editing.label}` : 'Add address'}
        >
          <form onSubmit={save}>
            <Select
              label="Client"
              value={form.clientId}
              onChange={(e) => setForm({ ...form, clientId: e.target.value })}
              hint="Choose “No client” for your own hub or depot — crew clock in there"
            >
              <option value="">No client (internal hub)</option>
              {(clients ?? []).map((c) => (
                <option key={c.id} value={c.id}>{c.legalName}</option>
              ))}
            </Select>
            <Input
              label="Label"
              value={form.label}
              onChange={(e) => setForm({ ...form, label: e.target.value })}
              required
              placeholder="Main Warehouse"
            />
            <div className="autocomplete-wrap">
              <Input
                label="Address line 1"
                value={form.line1}
                onChange={(e) => setForm({ ...form, line1: e.target.value })}
                required
                placeholder="123 Industrial Ave, Brgy San Isidro"
                hint={autocompleteLoading ? 'Searching…' : undefined}
              />
              {predictions.length > 0 && (
                <ul className="autocomplete-list">
                  {predictions.map((p) => (
                    <li key={p.placeId}>
                      <button type="button" onClick={() => selectPrediction(p)}>
                        {p.description}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="grid-2">
              <Input
                label="City / Municipality"
                value={form.cityMunicipality}
                onChange={(e) => setForm({ ...form, cityMunicipality: e.target.value })}
                placeholder="Makati City"
              />
              <Input
                label="Province"
                value={form.province}
                onChange={(e) => setForm({ ...form, province: e.target.value })}
                placeholder="Metro Manila"
              />
            </div>
            <Input
              label="Postal code"
              value={form.postalCode}
              onChange={(e) => setForm({ ...form, postalCode: e.target.value })}
              placeholder="1200"
            />
            <div className="grid-2">
              <Input
                label="Contact person"
                value={form.contactPerson}
                onChange={(e) => setForm({ ...form, contactPerson: e.target.value })}
              />
              <Input
                label="Contact number"
                value={form.contactNumber}
                onChange={(e) => setForm({ ...form, contactNumber: e.target.value })}
              />
            </div>
            <p className="text-muted" style={{ fontSize: '0.8125rem' }}>
              {editing
                ? 'If you change the address, its map location and service zone are found again when you save. Trips already booked are not changed.'
                : 'The map location and service zone are found automatically when you save.'}
            </p>
            {error && <p className="text-urgent">{error}</p>}
            <Button type="submit" loading={saving}>{editing ? 'Save changes' : 'Save address'}</Button>
          </form>
        </Modal>

        <style>{`
          .filter-row { max-width: 320px; margin-bottom: var(--space-md); }
          .panel {
            background: var(--color-surface);
            border: 1px solid var(--color-border);
            border-radius: var(--radius-md);
            padding: var(--space-lg);
            margin-top: var(--space-lg);
          }
          .panel h2 { margin-bottom: var(--space-xs); }
          .import-grid {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: var(--space-md);
            align-items: start;
          }
          @media (max-width: 640px) {
            .import-grid { grid-template-columns: 1fr; }
          }
          .csv-textarea {
            width: 100%;
            font-family: var(--font-mono, monospace);
            font-size: 0.8125rem;
            padding: var(--space-sm) var(--space-md);
            border: 1px solid var(--color-border);
            border-radius: var(--radius-md);
            background: var(--color-surface);
            color: var(--color-text);
            margin-bottom: var(--space-md);
            resize: vertical;
          }
          .grid-2 {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: var(--space-md);
          }
          .autocomplete-wrap { position: relative; }
          .autocomplete-list {
            position: absolute;
            z-index: 5;
            top: 100%;
            left: 0;
            right: 0;
            margin: -8px 0 0;
            padding: var(--space-xs) 0;
            list-style: none;
            background: var(--color-surface);
            border: 1px solid var(--color-border);
            border-radius: var(--radius-md);
            box-shadow: var(--shadow-md);
            max-height: 200px;
            overflow-y: auto;
          }
          .autocomplete-list li button {
            display: block;
            width: 100%;
            text-align: left;
            padding: var(--space-sm) var(--space-md);
            border: none;
            background: transparent;
            cursor: pointer;
            font-size: 0.875rem;
            color: var(--color-text);
          }
          .autocomplete-list li button:hover { background: var(--color-sand); }
        `}</style>
      </div>
    </Layout>
  );
}
